import React from "react";
import { Link } from "react-router-dom";
import { LogIn, User, LogOut, Ticket } from "lucide-react";
import { useMember } from "../../context/MemberAuthContext";

function Avatar({ member }) {
  const initial = (member.name || member.email || "?").trim().charAt(0).toUpperCase();
  if (member.picture) {
    return <img src={member.picture} alt={member.name || "Member"} className="w-8 h-8 rounded-full object-cover ring-1 ring-gold/40" referrerPolicy="no-referrer" />;
  }
  return <span className="grid place-items-center w-8 h-8 rounded-full bg-forest text-cream text-sm font-semibold">{initial}</span>;
}

export default function MemberMenu({ mobile = false }) {
  const { member, signIn, signOut } = useMember();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!member) {
    if (mobile) {
      return (
        <button onClick={signIn} data-testid="signin-google-button-mobile" className="w-full inline-flex items-center justify-center gap-2 bg-forest text-cream font-semibold py-2.5 rounded-full">
          <LogIn className="w-4 h-4" /> Sign in with Google
        </button>
      );
    }
    return (
      <button onClick={signIn} data-testid="signin-google-button" className="inline-flex items-center gap-2 border border-parchment rounded-full px-4 py-2 text-sm font-medium text-ink hover:border-gold hover:text-forest transition-colors">
        <LogIn className="w-4 h-4" /> Sign in
      </button>
    );
  }

  if (mobile) {
    return (
      <div className="space-y-1" data-testid="member-menu-mobile">
        <div className="flex items-center gap-2 py-2">
          <Avatar member={member} />
          <span className="text-sm font-medium text-ink truncate">{member.name || member.email}</span>
        </div>
        <Link to="/account" className="flex items-center gap-2 py-2 text-ink-muted" data-testid="member-account-link-mobile"><Ticket className="w-4 h-4" /> My registrations & orders</Link>
        <button onClick={signOut} className="flex items-center gap-2 py-2 text-ink-muted" data-testid="member-signout-mobile"><LogOut className="w-4 h-4" /> Sign out</button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref} data-testid="member-menu">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-sand transition-colors" data-testid="member-avatar-button">
        <Avatar member={member} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-cream border border-parchment rounded-lg shadow-xl shadow-ink/10 p-2 animate-fade-up z-50">
          <div className="px-3 py-2 border-b border-parchment mb-1">
            <p className="text-sm font-semibold text-ink truncate">{member.name || "Member"}</p>
            <p className="text-xs text-ink-muted truncate">{member.email}</p>
          </div>
          <Link to="/account" onClick={() => setOpen(false)} className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-ink-muted hover:bg-sand hover:text-forest transition-colors" data-testid="member-account-link">
            <User className="w-4 h-4" /> My account
          </Link>
          <button onClick={() => { setOpen(false); signOut(); }} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-ink-muted hover:bg-sand hover:text-forest transition-colors" data-testid="member-signout">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}
